import { ref, computed, reactive } from 'vue';
import { useSdk } from './useSdk';

export interface UserSetting {
  name: string;
  type: 'boolean' | 'string';
  value: string;
  description: string;
}

export interface UserSettingsResponse {
  data: UserSetting[];
}

function normalizeUserSettingsResponse(raw: unknown): UserSetting[] {
  if (Array.isArray(raw)) {
    return raw as UserSetting[];
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.data)) {
      return o.data as UserSetting[];
    }
    if (Array.isArray(o.settings)) {
      return o.settings as UserSetting[];
    }
  }
  return [];
}

export interface NotificationSettings {
  pushNotifications: boolean;
  comment: boolean;
  follow: boolean;
  followAccepted: boolean;
  followRequest: boolean;
  mention: boolean;
  reaction: boolean;
  repost: boolean;
  repostWithComment: boolean;
}

// Email notification settings mirror the notification settings but for email channel
export interface EmailNotificationSettings {
  comment: boolean;
  follow: boolean;
  followAccepted: boolean;
  followRequest: boolean;
  mention: boolean;
  reaction: boolean;
  repost: boolean;
  repostWithComment: boolean;
}

export interface GeneralSettings {
  defaultFeed: 'following' | 'forYou' | 'all';
  commentSort: 'popular' | 'recent';
  primaryColors: string;
  secondaryColors: string;
}

export type DigestFrequency = 'realtime' | 'daily' | 'weekly' | 'off';

/** Per-type app + per-type email only (excludes master push, marketing / digest). */
const BULK_NOTIFICATION_SETTING_KEYS = [
  'notifications_comment',
  'notifications_follow',
  'notifications_follow_accepted',
  'notifications_follow_request',
  'notifications_mention',
  'notifications_reaction',
  'notifications_repost',
  'notifications_repost_with_comment',
  'email_notifications_comment',
  'email_notifications_follow',
  'email_notifications_follow_accepted',
  'email_notifications_follow_request',
  'email_notifications_mention',
  'email_notifications_reaction',
  'email_notifications_repost',
  'email_notifications_repost_with_comment',
] as const;

export interface EmailSettings {
  marketingEnabled: boolean;
  digestEnabled: boolean;
  digestFrequency: DigestFrequency;
  activityEnabled: boolean;
}

export function useUserSettings() {
  const { sdk } = useSdk();
  const settings = ref<UserSetting[]>([]);
  const isLoading = ref(false);
  const isSaving = ref(false);

  // Pending changes buffer — keys are setting names, values are what the user picked
  const pendingChanges = reactive<Record<string, string | boolean>>({});

  const hasPendingChanges = computed(() => Object.keys(pendingChanges).length > 0);

  /**
   * Read the effective value of a setting: pending change wins, then server value.
   */
  function getEffective(name: string): string | undefined {
    if (name in pendingChanges) {
      const v = pendingChanges[name];
      return typeof v === 'boolean' ? (v ? 'true' : 'false') : v;
    }
    return settings.value.find(s => s.name === name)?.value;
  }

  function getEffectiveBool(name: string, defaultVal = false): boolean {
    const v = getEffective(name);
    if (v === undefined) return defaultVal;
    return v === 'true' || v === '1';
  }

  function getEffectiveString(name: string, defaultVal = ''): string {
    return getEffective(name) ?? defaultVal;
  }

  // Computed notification settings (App/Push notifications)
  const notificationSettings = computed<NotificationSettings>(() => ({
    pushNotifications: getEffectiveBool('notifications_push_notifications'),
    comment: getEffectiveBool('notifications_comment'),
    follow: getEffectiveBool('notifications_follow'),
    followAccepted: getEffectiveBool('notifications_follow_accepted'),
    followRequest: getEffectiveBool('notifications_follow_request'),
    mention: getEffectiveBool('notifications_mention'),
    reaction: getEffectiveBool('notifications_reaction'),
    repost: getEffectiveBool('notifications_repost'),
    repostWithComment: getEffectiveBool('notifications_repost_with_comment'),
  }));

  // Computed email notification settings (per-type email preferences)
  const emailNotificationSettings = computed<EmailNotificationSettings>(() => ({
    comment: getEffectiveBool('email_notifications_comment', true),
    follow: getEffectiveBool('email_notifications_follow', false),
    followAccepted: getEffectiveBool('email_notifications_follow_accepted', false),
    followRequest: getEffectiveBool('email_notifications_follow_request', true),
    mention: getEffectiveBool('email_notifications_mention', true),
    reaction: getEffectiveBool('email_notifications_reaction', false),
    repost: getEffectiveBool('email_notifications_repost', false),
    repostWithComment: getEffectiveBool('email_notifications_repost_with_comment', true),
  }));

  // Computed general settings
  const generalSettings = computed<GeneralSettings>(() => ({
    defaultFeed: (getEffectiveString('default_feed', 'following')) as 'following' | 'forYou' | 'all',
    commentSort: (getEffectiveString('comment_sort', 'popular')) as 'popular' | 'recent',
    primaryColors: getEffectiveString('primary_colors'),
    secondaryColors: getEffectiveString('secondary_colors'),
  }));

  // Computed email settings
  const emailSettings = computed<EmailSettings>(() => ({
    marketingEnabled: getEffectiveBool('email_marketing_enabled', true),
    digestEnabled: getEffectiveBool('email_digest_enabled', true),
    digestFrequency: getEffectiveString('email_digest_frequency', 'weekly') as DigestFrequency,
    activityEnabled: getEffectiveBool('email_activity_enabled', true),
  }));

  /**
   * Load user settings from the API
   */
  async function loadSettings(): Promise<void> {
    isLoading.value = true;
    try {
      const response = await sdk.getUserSettings();
      settings.value = normalizeUserSettingsResponse(response);
    } catch (error) {
      console.error('Failed to load user settings:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Stage a setting change locally (no network call).
   */
  function stageSetting(name: string, value: string | boolean): void {
    pendingChanges[name] = value;
  }

  /**
   * Flush all pending changes to the API in a single request.
   * Returns silently if there are no pending changes.
   */
  async function flushPendingChanges(): Promise<void> {
    const changes = { ...pendingChanges };
    if (Object.keys(changes).length === 0) return;

    isSaving.value = true;
    try {
      await sdk.updateUserSettings(changes);

      // Apply to local settings array
      const updated = [...settings.value];
      for (const [name, value] of Object.entries(changes)) {
        const stringValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
        const idx = updated.findIndex(s => s.name === name);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], value: stringValue };
        } else {
          updated.push({
            name,
            type: typeof value === 'boolean' ? 'boolean' : 'string',
            value: stringValue,
            description: '',
          });
        }
      }
      settings.value = updated;

      // Clear only the changes we just flushed
      for (const key of Object.keys(changes)) {
        delete pendingChanges[key];
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  }

  /**
   * Update a single setting immediately (for settings outside the notifications section).
   */
  async function updateSetting(name: string, value: string | boolean): Promise<void> {
    isSaving.value = true;
    try {
      await sdk.updateUserSettings({ [name]: value });

      const stringValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;
      const settingIndex = settings.value.findIndex(s => s.name === name);
      if (settingIndex >= 0) {
        settings.value[settingIndex] = { ...settings.value[settingIndex], value: stringValue };
      } else {
        settings.value = [...settings.value, {
          name,
          type: typeof value === 'boolean' ? 'boolean' : 'string',
          value: stringValue,
          description: '',
        }];
      }
    } catch (error) {
      console.error('Failed to update setting:', error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  }

  /**
   * Stage a notification setting change (App/Push)
   */
  function stageNotificationSetting(
    type: keyof NotificationSettings,
    enabled: boolean,
  ): void {
    const settingMap: Record<keyof NotificationSettings, string> = {
      pushNotifications: 'notifications_push_notifications',
      comment: 'notifications_comment',
      follow: 'notifications_follow',
      followAccepted: 'notifications_follow_accepted',
      followRequest: 'notifications_follow_request',
      mention: 'notifications_mention',
      reaction: 'notifications_reaction',
      repost: 'notifications_repost',
      repostWithComment: 'notifications_repost_with_comment',
    };

    const settingName = settingMap[type];
    if (settingName) {
      stageSetting(settingName, enabled);
    }
  }

  /**
   * Stage an email notification setting change (per-type)
   */
  function stageEmailNotificationSetting(
    type: keyof EmailNotificationSettings,
    enabled: boolean,
  ): void {
    const settingMap: Record<keyof EmailNotificationSettings, string> = {
      comment: 'email_notifications_comment',
      follow: 'email_notifications_follow',
      followAccepted: 'email_notifications_follow_accepted',
      followRequest: 'email_notifications_follow_request',
      mention: 'email_notifications_mention',
      reaction: 'email_notifications_reaction',
      repost: 'email_notifications_repost',
      repostWithComment: 'email_notifications_repost_with_comment',
    };

    const settingName = settingMap[type];
    if (settingName) {
      stageSetting(settingName, enabled);
    }
  }

  /**
   * Stage an email preference setting change
   */
  function stageEmailSetting(
    type: keyof EmailSettings,
    value: boolean | DigestFrequency,
  ): void {
    const settingMap: Record<keyof EmailSettings, string> = {
      marketingEnabled: 'email_marketing_enabled',
      digestEnabled: 'email_digest_enabled',
      digestFrequency: 'email_digest_frequency',
      activityEnabled: 'email_activity_enabled',
    };

    const settingName = settingMap[type];
    if (settingName) {
      stageSetting(settingName, value);
    }
  }

  /**
   * Stage all per-type app and email notification toggles.
   */
  function stageAllNotificationToggles(enabled: boolean): void {
    for (const key of BULK_NOTIFICATION_SETTING_KEYS) {
      pendingChanges[key] = enabled;
    }
  }

  /**
   * Discard all pending changes and revert to server state.
   */
  function discardPendingChanges(): void {
    for (const key of Object.keys(pendingChanges)) {
      delete pendingChanges[key];
    }
  }

  return {
    settings,
    isLoading,
    isSaving,
    hasPendingChanges,
    pendingChanges,
    notificationSettings,
    emailNotificationSettings,
    generalSettings,
    emailSettings,
    loadSettings,
    stageSetting,
    stageNotificationSetting,
    stageEmailNotificationSetting,
    stageEmailSetting,
    stageAllNotificationToggles,
    flushPendingChanges,
    discardPendingChanges,
    updateSetting,
  };
}
